import * as math from './math';
import { compute } from './compute';
import { OHLCBar, VolumeBar } from './types';

/**
 * Calculates Donchian Channel for an array of bars
 * @param bars Array of bars with at least high and low fields
 * @param period Donchian Channel period (default: 20)
 * @returns Array of bars with upper and lower band values
 */
export const donchian = <T extends Pick<OHLCBar, 'high' | 'low'>>(
    bars: T[],
    period: number = 20
): Array<T & { upperBand: number | undefined; lowerBand: number | undefined }> => {
    // Compute upper band
    const barsWithUpper = compute(
        bars,
        'upperBand',
        (bar, index, allBars, params) => {
            if (index < params.period - 1) {
                return undefined;
            }

            const slice = allBars.slice(index - params.period + 1, index + 1);
            return Math.max(...slice.map(b => b.high));
        },
        { period }
    );

    // Compute lower band
    return compute(
        barsWithUpper,
        'lowerBand',
        (bar, index, allBars, params) => {
            if (index < params.period - 1) {
                return undefined;
            }

            const slice = allBars.slice(index - params.period + 1, index + 1);
            return Math.min(...slice.map(b => b.low));
        },
        { period }
    ) as Array<T & { upperBand: number | undefined; lowerBand: number | undefined }>;
};

/**
 * Calculates the True Range (TR) and Average True Range (ATR) for an array of bars
 * @param bars Array of bars with OHLC data
 * @param period Period for ATR calculation (default: 14)
 * @returns Array of bars with TR, ATR, and ATR average values
 */
export const atr = <T extends OHLCBar>(
    bars: T[],
    period: number = 14
): Array<T & {
    tr: number | undefined;
    atr: number | undefined;
    atrAvg: number | undefined;
}> => {
    // Calculate True Range for each bar
    const barsWithTR = compute(
        bars,
        'tr',
        (bar, index, allBars) => {
            if (index === 0) {
                return bar.high - bar.low; // Initial TR is just the high-low range
            }

            const previousBar = allBars[index - 1];
            const highLow = bar.high - bar.low;
            const highPrevClose = Math.abs(bar.high - previousBar.close);
            const lowPrevClose = Math.abs(bar.low - previousBar.close);

            return Math.max(highLow, highPrevClose, lowPrevClose);
        }
    );

    // Calculate ATR (Average True Range)
    const barsWithATR = compute(
        barsWithTR,
        'atr',
        (bar, index, allBars, params) => {
            if (index < params.period) {
                return undefined;
            }

            if (index === params.period) {
                // First ATR is simple average of TR values
                let totalTR = 0;
                for (let i = 1; i <= params.period; i++) {
                    totalTR += allBars[i].tr as number;
                }
                return totalTR / params.period;
            }

            // Subsequent ATRs use smoothing formula
            const prevATR = (allBars[index - 1] as T & { atr?: number }).atr as number;
            return ((prevATR * (params.period - 1)) + (bar.tr as number)) / params.period;
        },
        { period }
    );

    // Calculate average ATR over a longer period to detect volatility expansion
    const barsWithATRAvg = compute(
        barsWithATR,
        'atrAvg',
        (bar, index, allBars, params) => {
            if (index < params.period * 2) {
                return undefined;
            }

            let totalATR = 0;
            for (let i = index - params.period + 1; i <= index; i++) {
                if (allBars[i].atr !== undefined) {
                    totalATR += allBars[i].atr as number;
                }
            }
            return totalATR / params.period;
        },
        { period }
    );

    return barsWithATRAvg as Array<T & {
        tr: number | undefined;
        atr: number | undefined;
        atrAvg: number | undefined;
    }>;
};

/**
 * Calculates On-Balance Volume (OBV)
 * @param bars Array of bars with volume data
 * @param field Field to use for price direction (default: 'close')
 * @returns Array of bars with OBV values
 */
export const obv = <T extends VolumeBar>(
    bars: T[],
    field: keyof T = 'close' as keyof T
): Array<T & { obv: number | undefined }> => {
    if (bars.length === 0) {
        return [];
    }

    return compute(
        bars,
        'obv',
        (bar, index, allBars) => {
            if (index === 0) {
                return bar.volume; // Initial OBV value
            }

            const prevBar = allBars[index - 1];
            const prevOBV = (prevBar as T & { obv?: number }).obv as number;

            // If close > prev close, add volume
            if ((bar[field] as any) > (prevBar[field] as any)) {
                return prevOBV + bar.volume;
            }
            // If close < prev close, subtract volume
            else if ((bar[field] as any) < (prevBar[field] as any)) {
                return prevOBV - bar.volume;
            }
            // If close unchanged, OBV unchanged
            else {
                return prevOBV;
            }
        }
    );
};

/**
 * Calculates OBV moving average
 * @param bars Array of bars with volume data
 * @param period Period for the moving average (default: 20)
 * @returns Array of bars with OBV and OBV MA values
 */
export const obvMA = <T extends VolumeBar>(
    bars: T[],
    period: number = 20
): Array<T & { obv: number | undefined; obvMA: number | undefined }> => {
    if (bars.length === 0) {
        return [];
    }

    // First ensure we have OBV values
    const barsWithOBV = obv(bars);

    return compute(
        barsWithOBV,
        'obvMA',
        (bar, index, allBars, params) => {
            if (index < params.period - 1) {
                return undefined;
            }

            let sum = 0;
            for (let i = index - params.period + 1; i <= index; i++) {
                sum += (allBars[i] as any).obv;
            }
            return sum / params.period;
        },
        { period }
    ) as Array<T & { obv: number | undefined; obvMA: number | undefined }>;
};